var _class,
  _Audio,
  _jsxFileName = "/var/home/clovis/Documents/Mangane/app/soapbox/features/audio/index.js";
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import Icon from 'soapbox/components/icon';
import { formatTime, getPointerPosition, fileNameFromURL } from 'soapbox/features/video';
import Visualizer from './visualizer';
const messages = defineMessages({
  play: {
    "id": "video.play",
    "defaultMessage": "Play"
  },
  pause: {
    "id": "video.pause",
    "defaultMessage": "Pause"
  },
  mute: {
    "id": "video.mute",
    "defaultMessage": "Mute sound"
  },
  unmute: {
    "id": "video.unmute",
    "defaultMessage": "Unmute sound"
  },
  download: {
    "id": "video.download",
    "defaultMessage": "Download file"
  }
});
const TICK_SIZE = 10;
const PADDING = 180;
let Audio = injectIntl(_class = (_Audio = class Audio extends React.PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "state", {
      width: this.props.width,
      currentTime: 0,
      buffer: 0,
      duration: null,
      paused: true,
      muted: false,
      volume: 0.5,
      dragging: false
    });
    _defineProperty(this, "setPlayerRef", c => {
      this.player = c;
      if (this.player) {
        this._setDimensions();
      }
    });
    _defineProperty(this, "setSeekRef", c => {
      this.seek = c;
    });
    _defineProperty(this, "setVolumeRef", c => {
      this.volume = c;
    });
    _defineProperty(this, "setAudioRef", c => {
      this.audio = c;
      if (this.audio) {
        this.setState({
          volume: this.audio.volume,
          muted: this.audio.muted
        });
      }
    });
    _defineProperty(this, "setCanvasRef", c => {
      this.canvas = c;
      this.visualizer.setCanvas(c);
    });
    _defineProperty(this, "togglePlay", () => {
      if (!this.audioContext) {
        this._initAudioContext();
      }
      if (this.state.paused) {
        this.setState({
          paused: false
        }, () => this.audio.play());
      } else {
        this.setState({
          paused: true
        }, () => this.audio.pause());
      }
    });
    _defineProperty(this, "handleResize", debounce(() => {
      if (this.player) {
        this._setDimensions();
      }
    }, 250, {
      trailing: true
    }));
    _defineProperty(this, "handlePlay", () => {
      this.setState({
        paused: false
      });
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
      this._renderCanvas();
    });
    _defineProperty(this, "handlePause", () => {
      this.setState({
        paused: true
      });
      if (this.audioContext) {
        this.audioContext.suspend();
      }
    });
    _defineProperty(this, "handleProgress", () => {
      const lastTimeRange = this.audio.buffered.length - 1;
      if (lastTimeRange > -1) {
        this.setState({
          buffer: Math.ceil(this.audio.buffered.end(lastTimeRange) / this.audio.duration * 100)
        });
      }
    });
    _defineProperty(this, "toggleMute", () => {
      const muted = !this.state.muted;
      this.setState({
        muted
      }, () => {
        this.audio.muted = muted;
      });
    });
    _defineProperty(this, "handleVolumeMouseDown", e => {
      document.addEventListener('mousemove', this.handleMouseVolSlide, true);
      document.addEventListener('mouseup', this.handleVolumeMouseUp, true);
      document.addEventListener('touchmove', this.handleMouseVolSlide, true);
      document.addEventListener('touchend', this.handleVolumeMouseUp, true);
      this.handleMouseVolSlide(e);
      e.preventDefault();
      e.stopPropagation();
    });
    _defineProperty(this, "handleVolumeMouseUp", () => {
      document.removeEventListener('mousemove', this.handleMouseVolSlide, true);
      document.removeEventListener('mouseup', this.handleVolumeMouseUp, true);
      document.removeEventListener('touchmove', this.handleMouseVolSlide, true);
      document.removeEventListener('touchend', this.handleVolumeMouseUp, true);
    });
    _defineProperty(this, "handleMouseDown", e => {
      document.addEventListener('mousemove', this.handleMouseMove, true);
      document.addEventListener('mouseup', this.handleMouseUp, true);
      document.addEventListener('touchmove', this.handleMouseMove, true);
      document.addEventListener('touchend', this.handleMouseUp, true);
      this.setState({
        dragging: true
      });
      this.audio.pause();
      this.handleMouseMove(e);
      e.preventDefault();
      e.stopPropagation();
    });
    _defineProperty(this, "handleMouseUp", () => {
      document.removeEventListener('mousemove', this.handleMouseMove, true);
      document.removeEventListener('mouseup', this.handleMouseUp, true);
      document.removeEventListener('touchmove', this.handleMouseMove, true);
      document.removeEventListener('touchend', this.handleMouseUp, true);
      this.setState({
        dragging: false
      });
      this.audio.play();
    });
    _defineProperty(this, "handleMouseMove", throttle(e => {
      const {
        x
      } = getPointerPosition(this.seek, e);
      const currentTime = this.audio.duration * x;
      if (!isNaN(currentTime)) {
        this.setState({
          currentTime
        }, () => {
          this.audio.currentTime = currentTime;
        });
      }
    }, 15));
    _defineProperty(this, "handleTimeUpdate", () => {
      this.setState({
        currentTime: this.audio.currentTime,
        duration: this.audio.duration
      });
    });
    _defineProperty(this, "handleMouseVolSlide", throttle(e => {
      const {
        x
      } = getPointerPosition(this.volume, e);
      if (!isNaN(x)) {
        this.setState({
          volume: x
        }, () => {
          this.audio.volume = x;
        });
      }
    }, 15));
    _defineProperty(this, "handleScroll", throttle(() => {
      if (!this.canvas || !this.audio) {
        return;
      }
      const {
        top,
        height
      } = this.canvas.getBoundingClientRect();
      const inView = top <= (window.innerHeight || document.documentElement.clientHeight) && top + height >= 0;
      if (!this.state.paused && !inView) {
        this.audio.pause();
        if (this.props.deployPictureInPicture) {
          this.props.deployPictureInPicture('audio', this._pack());
        }
        this.setState({
          paused: true
        });
      }
    }, 150, {
      trailing: true
    }));
    _defineProperty(this, "handleMouseEnter", () => {
      this.setState({
        hovered: true
      });
    });
    _defineProperty(this, "handleMouseLeave", () => {
      this.setState({
        hovered: false
      });
    });
    _defineProperty(this, "handleLoadedData", () => {
      const {
        autoPlay,
        currentTime,
        volume,
        muted
      } = this.props;
      this.setState({
        duration: this.audio.duration
      });
      if (currentTime) {
        this.audio.currentTime = currentTime;
      }
      if (volume !== undefined) {
        this.audio.volume = volume;
      }
      if (muted !== undefined) {
        this.audio.muted = muted;
      }
      if (autoPlay) {
        this.togglePlay();
      }
    });
    _defineProperty(this, "handleDownload", () => {
      fetch(this.props.src).then(res => res.blob()).then(blob => {
        const element = document.createElement('a');
        const objectURL = URL.createObjectURL(blob);
        element.setAttribute('href', objectURL);
        element.setAttribute('download', fileNameFromURL(this.props.src));
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        URL.revokeObjectURL(objectURL);
      }).catch(err => {
        console.error(err);
      });
    });
    _defineProperty(this, "handleAudioKeyDown", e => {
      // On the audio element or the seek bar, we can safely use the space bar
      // for playback control because there are no buttons to press

      if (e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        this.togglePlay();
      }
    });
    _defineProperty(this, "handleKeyDown", e => {
      switch (e.key) {
        case 'k':
          e.preventDefault();
          e.stopPropagation();
          this.togglePlay();
          break;
        case 'm':
          e.preventDefault();
          e.stopPropagation();
          this.toggleMute();
          break;
        case 'j':
          e.preventDefault();
          e.stopPropagation();
          this.seekBy(-10);
          break;
        case 'l':
          e.preventDefault();
          e.stopPropagation();
          this.seekBy(10);
          break;
      }
    });
    this.visualizer = new Visualizer(TICK_SIZE);
  }
  _pack() {
    return {
      src: this.props.src,
      volume: this.audio.volume,
      muted: this.audio.muted,
      currentTime: this.audio.currentTime,
      poster: this.props.poster,
      backgroundColor: this.props.backgroundColor,
      foregroundColor: this.props.foregroundColor,
      accentColor: this.props.accentColor
    };
  }
  _setDimensions() {
    const width = this.player.offsetWidth;
    const height = this.props.fullscreen ? this.player.offsetHeight : width / (16 / 9);
    if (this.props.cacheWidth) {
      this.props.cacheWidth(width);
    }
    this.setState({
      width,
      height
    });
  }
  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize, {
      passive: true
    });
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.src !== this.props.src || this.state.width !== prevState.width || this.state.height !== prevState.height || prevProps.accentColor !== this.props.accentColor) {
      this._clear();
      this._draw();
    }
  }
  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    if (!this.state.paused && this.audio && this.props.deployPictureInPicture) {
      this.props.deployPictureInPicture('audio', this._pack());
    }
  }
  _initAudioContext() {
    // eslint-disable-next-line compat/compat
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const context = new AudioContext();
    const source = context.createMediaElementSource(this.audio);
    this.visualizer.setAudioContext(context, source);
    source.connect(context.destination);
    this.audioContext = context;
  }
  _renderCanvas() {
    requestAnimationFrame(() => {
      if (!this.audio) return;
      this.handleTimeUpdate();
      this._clear();
      this._draw();
      if (!this.state.paused) {
        this._renderCanvas();
      }
    });
  }
  _clear() {
    this.visualizer.clear(this.state.width, this.state.height);
  }
  _draw() {
    this.visualizer.draw(this._getCX(), this._getCY(), this._getAccentColor(), this._getRadius(), this._getScaleCoefficient());
  }
  _getRadius() {
    return parseInt(((this.state.height || this.props.height) - PADDING * this._getScaleCoefficient() * 2) / 2);
  }
  _getScaleCoefficient() {
    return (this.state.height || this.props.height) / 982;
  }
  _getCX() {
    return Math.floor(this.state.width / 2) || null;
  }
  _getCY() {
    return Math.floor(this._getRadius() + PADDING * this._getScaleCoefficient()) || null;
  }
  _getAccentColor() {
    return this.props.accentColor || '#ffffff';
  }
  _getBackgroundColor() {
    return this.props.backgroundColor || '#000000';
  }
  _getForegroundColor() {
    return this.props.foregroundColor || '#ffffff';
  }
  seekBy(time) {
    const currentTime = this.audio.currentTime + time;
    if (!isNaN(currentTime)) {
      this.setState({
        currentTime
      }, () => {
        this.audio.currentTime = currentTime;
      });
    }
  }
  render() {
    const {
      src,
      intl,
      alt,
      editable
    } = this.props;
    const {
      paused,
      muted,
      volume,
      currentTime,
      buffer,
      dragging
    } = this.state;
    const duration = this.state.duration || this.props.duration;
    const progress = Math.min(currentTime / duration * 100, 100);
    return /*#__PURE__*/React.createElement("div", {
      className: classNames('audio-player', {
        editable
      }),
      ref: this.setPlayerRef,
      style: {
        backgroundColor: this._getBackgroundColor(),
        color: this._getForegroundColor(),
        width: '100%',
        height: this.props.fullscreen ? '100%' : this.state.height || this.props.height
      },
      onMouseEnter: this.handleMouseEnter,
      onMouseLeave: this.handleMouseLeave,
      tabIndex: "0",
      onKeyDown: this.handleKeyDown,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 446,
        columnNumber: 7
      }
    }, /*#__PURE__*/React.createElement("audio", {
      src: src,
      ref: this.setAudioRef,
      preload: "auto",
      onPlay: this.handlePlay,
      onPause: this.handlePause,
      onProgress: this.handleProgress,
      onLoadedData: this.handleLoadedData,
      crossOrigin: "anonymous",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 447,
        columnNumber: 9
      }
    }), /*#__PURE__*/React.createElement("canvas", {
      role: "button",
      tabIndex: "0",
      className: "audio-player__canvas",
      width: this.state.width,
      height: this.state.height,
      style: {
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0
      },
      ref: this.setCanvasRef,
      onClick: this.togglePlay,
      onKeyDown: this.handleAudioKeyDown,
      title: alt,
      "aria-label": alt,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 458,
        columnNumber: 9
      }
    }), this.props.poster && /*#__PURE__*/React.createElement("img", {
      src: this.props.poster,
      alt: "",
      width: (this._getRadius() - TICK_SIZE) * 2 || null,
      height: (this._getRadius() - TICK_SIZE) * 2 || null,
      style: {
        position: 'absolute',
        left: this._getCX(),
        top: this._getCY(),
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        pointerEvents: 'none'
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 472,
        columnNumber: 31
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "video-player__seek",
      onMouseDown: this.handleMouseDown,
      ref: this.setSeekRef,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 480,
        columnNumber: 9
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "video-player__seek__buffer",
      style: {
        width: `${buffer}%`
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 481,
        columnNumber: 11
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "video-player__seek__progress",
      style: {
        width: `${progress}%`,
        backgroundColor: this._getAccentColor()
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 482,
        columnNumber: 11
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: classNames('video-player__seek__handle', {
        active: dragging
      }),
      tabIndex: "0",
      style: {
        left: `${progress}%`,
        backgroundColor: this._getAccentColor()
      },
      onKeyDown: this.handleAudioKeyDown,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 484,
        columnNumber: 11
      }
    })), /*#__PURE__*/React.createElement("div", {
      className: "video-player__controls active",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 492,
        columnNumber: 9
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "video-player__buttons-bar",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 493,
        columnNumber: 11
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "video-player__buttons left",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 494,
        columnNumber: 13
      }
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      title: intl.formatMessage(paused ? messages.play : messages.pause),
      "aria-label": intl.formatMessage(paused ? messages.play : messages.pause),
      className: "player-button",
      onClick: this.togglePlay,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 495,
        columnNumber: 15
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: paused ? require('@tabler/icons/player-play.svg') : require('@tabler/icons/player-pause.svg'),
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 495,
        columnNumber: 230
      }
    })), /*#__PURE__*/React.createElement("button", {
      type: "button",
      title: intl.formatMessage(muted ? messages.unmute : messages.mute),
      "aria-label": intl.formatMessage(muted ? messages.unmute : messages.mute),
      className: "player-button",
      onClick: this.toggleMute,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 496,
        columnNumber: 15
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: muted ? require('@tabler/icons/volume-3.svg') : require('@tabler/icons/volume.svg'),
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 496,
        columnNumber: 230
      }
    })), /*#__PURE__*/React.createElement("div", {
      className: classNames('video-player__volume', {
        active: this.state.hovered
      }),
      ref: this.setVolumeRef,
      onMouseDown: this.handleVolumeMouseDown,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 498,
        columnNumber: 15
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "video-player__volume__current",
      style: {
        width: `${volume * 100}%`,
        backgroundColor: this._getAccentColor()
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 499,
        columnNumber: 17
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "video-player__volume__handle",
      tabIndex: "0",
      style: {
        left: `${volume * 100}%`,
        backgroundColor: this._getAccentColor()
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 501,
        columnNumber: 17
      }
    })), /*#__PURE__*/React.createElement("span", {
      className: "video-player__time",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 508,
        columnNumber: 15
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "video-player__time-current",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 509,
        columnNumber: 17
      }
    }, formatTime(Math.floor(currentTime))), duration && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "video-player__time-sep",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 511,
        columnNumber: 19
      }
    }, "/"), /*#__PURE__*/React.createElement("span", {
      className: "video-player__time-total",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 512,
        columnNumber: 19
      }
    }, formatTime(Math.floor(duration)))))), /*#__PURE__*/React.createElement("div", {
      className: "video-player__buttons right",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 517,
        columnNumber: 13
      }
    }, /*#__PURE__*/React.createElement("a", {
      title: intl.formatMessage(messages.download),
      "aria-label": intl.formatMessage(messages.download),
      className: "video-player__download__icon player-button",
      href: this.props.src,
      download: true,
      target: "_blank",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 518,
        columnNumber: 15
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: require('@tabler/icons/download.svg'),
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 526,
        columnNumber: 17
      }
    }))))));
  }
}, _defineProperty(_Audio, "propTypes", {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  poster: PropTypes.string,
  duration: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  editable: PropTypes.bool,
  fullscreen: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  cacheWidth: PropTypes.func,
  backgroundColor: PropTypes.string,
  foregroundColor: PropTypes.string,
  accentColor: PropTypes.string,
  currentTime: PropTypes.number,
  autoPlay: PropTypes.bool,
  volume: PropTypes.number,
  muted: PropTypes.bool,
  deployPictureInPicture: PropTypes.func
}), _Audio)) || _class;
export { Audio as default };